import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { resolve } from 'url';
import { environment } from '../../../environments/environment';
import { DialogService } from './dialog.service';
import { tJWT } from './jwt.service';

@Injectable({
  providedIn: 'root'
})
export class HttpService {
  private jwt: tJWT = null;
  private renew: () => Promise<boolean> = null;

  constructor(private http: HttpClient, private dialog: DialogService) {}

  public async get<T = iAny>(
    base: baseURL,
    url: string | string[],
    auth: string | string[] | true | tJWT = null,
    params: { [key: string]: string | number } = {}
  ): Promise<T> {
    const urlEncoded = this.getUrl(base, url);
    const headers = await this.getAuthHeaders(auth);
    if (headers === false) {
      this.dialog.alert('שגיאת אבטחה', 'אינך מחובר למערכת. נסה לרענן את העמוד או להתחבר מחדש למשתמש.');
      return Promise.reject('User not loggedin');
    }

    return this.http
      .get<T>(urlEncoded, {
        params: params as any,
        headers
      })
      .toPromise();
  }

  public async post<T = iAny>(
    base: baseURL,
    url: string | string[],
    body: any,
    auth: string | string[] | true | tJWT = null,
    params: { [key: string]: string | number } = {}
  ): Promise<T> {
    const urlEncoded = this.getUrl(base, url);
    const headers = await this.getAuthHeaders(auth);
    if (headers === false) {
      this.dialog.alert('שגיאת אבטחה', 'אינך מחובר למערכת. נסה לרענן את העמוד או להתחבר מחדש למשתמש.');
      return Promise.reject('User not loggedin');
    }

    return this.http
      .post<T>(urlEncoded, body, {
        params: params as any,
        headers
      })
      .toPromise();
  }

  public async delete<T = iAny>(
    base: baseURL,
    url: string | string[],
    auth: string | string[] | true | tJWT = null,
    params: { [key: string]: string | number } = {}
  ): Promise<T> {
    const urlEncoded = this.getUrl(base, url);
    const headers = await this.getAuthHeaders(auth);
    if (headers === false) {
      this.dialog.alert('שגיאת אבטחה', 'אינך מחובר למערכת. נסה לרענן את העמוד או להתחבר מחדש למשתמש.');
      return Promise.reject('User not loggedin');
    }

    return this.http
      .delete<T>(urlEncoded, {
        params: params as any,
        headers
      })
      .toPromise();
  }

  public async put<T = iAny>(
    base: baseURL,
    url: string | string[],
    body: any,
    auth: string | string[] | true | tJWT = null,
    params: { [key: string]: string | number } = {}
  ): Promise<T> {
    const urlEncoded = this.getUrl(base, url);
    const headers = await this.getAuthHeaders(auth);
    if (headers === false) {
      this.dialog.alert('שגיאת אבטחה', 'אינך מחובר למערכת. נסה לרענן את העמוד או להתחבר מחדש למשתמש.');
      return Promise.reject('User not loggedin');
    }

    return this.http
      .put<T>(urlEncoded, body, {
        params: params as any,
        headers
      })
      .toPromise();
  }

  public getUrl(base: baseURL, url: string | string[]): string {
    if (Array.isArray(url)) url = (url as string[]).join('/');

    return resolve(environment.serverUrl[base], url.replace(/^\//, ''));
  }

  public async getAuthHeaders(
    auth: string | string[] | true | tJWT,
    renew: boolean = true
  ): Promise<{ Authorization?: string } | false> {
    if (!auth) return {};
    if (auth === true) {
      if (this.jwt && this.jwt.valid()) return { Authorization: 'Bearer ' + this.jwt.jwt };
      if (this.renew && renew) if (await this.renew()) return this.getAuthHeaders(auth, false);
      return false;
    }
    if (auth['jwt']) return (auth as tJWT).valid() ? { Authorization: 'Bearer ' + (auth as tJWT).jwt } : false;
    if (Array.isArray(auth) && auth.length == 2) {
      // Temporary fix - unescape is deprecated and should not be used.
      const base64encode = (str: string) => btoa(unescape(encodeURIComponent(str)));
      return { Authorization: 'basic ' + base64encode(auth[0] + ':' + auth[1]) }
    }
    if (!Array.isArray(auth)) return { Authorization: 'Bearer ' + auth };
    return {};
  }

  public alertError(error: HttpErrorResponse, errorText: iErrorDecoder = {}): Promise<void> {
    const err = this.decodeError(error, errorText);
    return this.dialog.alert('שגיאה', err.msg);
  }

  public decodeError(
    error: HttpErrorResponse,
    errorText: iErrorDecoder = {}
  ): { msg: string; status: number; error: HttpErrorResponse } {
    const errorNumber: {} = {
      403: 'אין ברשותך את ההרשאות הדרושות לביצוע הפעולה.',
      401: 'עליך להתחבר על מנת לקבל גישה לעמוד זה. אם הנך מחובר/ת כבר, יש להתנתק ולהתחבר בשנית.',
      400: 'הפעולה נכשלה. אנא בדוק את תקינות הפרטים ונסה שנית.',
      404: 'לא ניתן למצוא את מה שחיפשת.',
      409: 'הפעולה נכשלה בעקבות המתנה ארוכה מדיי. בדוק את החיבור שלך ונסה שנית, או פנה לתמיכה.',
      500: 'המערכת נכשלה בביצוע הפעולה. יש לנסות שנית.',
      0: 'הפעולה נכשלה. אנא בדוק את חיבור האינטרנט שלך ונסה שוב.'
    };

    Error['Resource not found'] = 'שגיאה פנימית. יש לעדכן את המתכנת על שגיאה זו.';

    if (!error.error) (error as any)['error'] = error;

    return {
      msg:
        (error.error.code && errorText[error.error.code]) ||
        (error.error.message && errorText[error.error.message]) ||
        errorText[error.status] ||
        (error.error.code && Error[error.error.code]) ||
        (error.error.message && Error[error.error.message]) ||
        Error[error.status] ||
        (!error.error.code && error.error.message && error.error.message.match(/[א-ת]/) && error.error.message) ||
        (error.error.code && errorNumber[error.error.code]) ||
        (error.error.message && errorNumber[error.error.message]) ||
        errorNumber[error.status] ||
        (error.error && error.error.code && Error[error.error.code]) ||
        (error.error && error.error.message) ||
        error.statusText,
      status: error.status,
      error
    };
  }

  public setJWT(jwt: tJWT, renew: () => Promise<boolean>): boolean {
    if (!jwt || !jwt.valid()) return false;
    this.jwt = jwt;
    this.renew = renew;
    return true;
  }

  public removeJWT() {
    this.jwt = null;
  }
}

export type iErrorDecoder = { [CodeOrStatus in (keyof typeof Error) | number]?: string };

export enum Error {
  // For any JWT
  INVALID_JWT = 'הפעולה נחסמה מטעמי אבטחה. יש לרענן ולנסות שנית, ואם הבעיה חוזרת על עצמה, להתנתק ולהתחבר מחדש.',
  UNAUTHORIZED_JWT = 'הפעולה נחסמה מטעמי אבטחה. יש לרענן ולנסות שנית, ואם הבעיה חוזרת על עצמה, להתנתק ולהתחבר מחדש.',

  // For any token
  NOT_FOUND_TOKEN = 'הפעולה נחסמה מטעמי אבטחה. יש לרענן ולנסות שנית, ואם הבעיה חוזרת על עצמה, להתנתק ולהתחבר מחדש.',
  EXPIRED_TOKEN = 'פג תוקפה של פעולה זו.',
  USED_TOKEN = 'פעולה זו הינה חד פעמית והושלמה בעבר.',

  // For admin code
  NOT_FOUND_CODE = 'הוזן קוד מנהל שגוי',
  EXPIRED_CODE = 'פג תוקפו של קוד מנהל זה',
  USED_CODE = 'קוד מנהל הינו קוד חד פעמי, ובוצע שימוש בקוד מנהל זה.',

  // Register errors
  EMAIL_USED = 'קיים משתמש עם כתובת דוא״ל זו',
  PASSWORD_TOO_WEAK = 'הסיסמה שהוזנה לא מספיק חזקה',
  INVALID_PHONE = 'מספר הטלפון שהוזן לא תקין',

  // Login errors
  USERNAME_OR_PASSWORD_INCORRECT = 'שם המשתמש או הסיסמה אינם נכונים',
  MFA_INCORRECT = 'הקוד הזמני שהוזן שגוי',
  USER_NOT_FOUND = 'המשתמש אליו היית מחובר אינו נמצא, וככל הנראה נמחק',
  MULTIPLE_PHONES = 'היות ובמערכת רשומים יותר ממשתמש אחד עם מספר טלפון זה, לא ניתן להשתמש בטלפון כמזהה אישי. על מנת להמשיך בתהליך ההתחברות, הזינו את כתובת הדוא״ל עימה נרשמתם למערכת ואת הסיסמה.',

  // Department errors
  DEPARTMENT_NAME_USED = 'קיים מדור עם שם זה במערכת זו',
  MUST_DELETE_USERS_BEFORE_DEPARTMENT_DELETE = 'יש למחוק או להעביר את כל המשתמשים מהמדור, לפני שניתן יהיה למחוק אותו.',

  // Recaptcha error
  HELLO_ROBOT = 'המערכת חושדת בכך שאתה רובוט, ומטעמי אבטחה, פעולה זו לא הושלמה. המערכת מתבססת על דרכי הפעולה שלך באתר ובאינטרנט בכלל, ולכן, אם הודעה זו חוזרת על עצמה יש לגלוש קצת באתרים אחרים או בעמודים אחרים באתר זה ולנסות שנית.',

  // Global errors
  SERVER_ERROR = 'המערכת נכשלה בביצוע הפעולה. יש לנסות שנית.',
  NOT_FOUND = 'לא ניתן למצוא את מה שחיפשת.',
  UNAUTHORIZED = 'אין ברשותך את ההרשאות הדרושות לביצוע הפעולה.',
  UNAUTHENTICATED = 'עליך להתחבר על מנת לקבל גישה לעמוד זה. אם הנך מחובר/ת כבר, יש להתנתק ולהתחבר בשנית.',
  BAD_REQUEST = 'הפעולה נכשלה. אנא בדוק את תקינות הפרטים ונסה שנית.'
}

type baseURL = keyof typeof environment['serverUrl'];
export interface iAny {
  [key: string]: any;
}
